// Interaction Types and Game Mechanics System

export enum InteractionType {
  // Collaborative Creation
  PLAN_DATE = 'plan_date',
  PLAN_TRIP = 'plan_trip', 
  PLAN_DREAMHOME = 'plan_dreamhome',
  FASHION_ADVICE = 'fashion_advice',
  
  // Games
  THIS_OR_THAT = 'this_or_that',
  ASSUMPTIONS = 'assumptions',
  RAPID_FIRE = 'rapid_fire',
  STORYTELLING = 'storytelling',
  
  // Q&A Systems
  PROFILE_REACTION = 'profile_reaction',
  CUSTOM_QUESTION = 'custom_question',
  DELAYED_RESPONSE = 'delayed_response',
  
  // Social
  ICEBREAKER = 'icebreaker',
  DEEP_DIVE = 'deep_dive',
  FUTURE_PLANNING = 'future_planning',
  
  // Meta
  UNIVERSE_INTRO = 'universe_intro',
  NAGPAL_SUMMON = 'nagpal_summon',
  END_CONVERSATION = 'end_conversation'
}

export interface GameState {
  type: InteractionType;
  active: boolean;
  participants: ('universe' | 'nagpal' | 'visitor')[];
  currentTurn?: 'universe' | 'nagpal' | 'visitor';
  round: number;
  maxRounds?: number;
  score?: {
    nagpal: number;
    visitor: number;
  };
  data?: any; // Game-specific data
  startTime: Date;
  lastActivity: Date;
}

// Date Planning Interaction
export interface DatePlanningData {
  location: string;
  timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night';
  budget: 'budget-friendly' | 'moderate' | 'special-occasion';
  vibe: 'casual' | 'romantic' | 'adventurous' | 'cultural';
  visitorSuggestions: string[];
  nagpalPreferences: string[];
  finalPlan?: {
    activities: string[];
    restaurants: string[];
    timeline: string[];
  };
}

// This or That Game
export interface ThisOrThatData {
  questions: Array<{
    question: string;
    optionA: string;
    optionB: string;
    nagpalChoice?: string;
    visitorChoice?: string;
    reasoning?: string;
  }>;
  currentQuestionIndex: number;
  matches: number; // How many choices matched
}

// Assumptions Game
export interface AssumptionsData {
  assumptions: Array<{
    assumption: string;
    correct: boolean;
    nagpalResponse: string;
    points: number;
  }>;
  totalPoints: number;
}

// Custom Question System
export interface CustomQuestionData {
  question: string;
  askerName?: string;
  emailForNotification?: string;
  responseDeadline?: Date;
  answered: boolean;
  answer?: string;
  followUpAllowed: boolean;
}

// Predefined Interaction Templates
export const InteractionTemplates = {
  [InteractionType.UNIVERSE_INTRO]: {
    prompt: "Welcome! I'm the Universe, and I'm here to help you both connect in magical ways. Think of me as your cosmic wingman! 🌌✨",
    options: [
      "Let's play a game to break the ice",
      "Want to plan something fun together?", 
      "Ask Nagpal a question",
      "Get to know each other better"
    ]
  },
  
  [InteractionType.PLAN_DATE]: {
    prompt: "Let's plan the perfect date together! I'll help coordinate between you and Nagpal to create something amazing.",
    phases: [
      "What kind of vibe are you feeling? (casual/romantic/adventurous/cultural)",
      "Perfect! Let me get Nagpal's thoughts on this...",
      "Here's what we're thinking - want to add anything?",
      "Amazing! Let me pull together the final plan with real details..."
    ]
  },
  
  [InteractionType.THIS_OR_THAT]: {
    prompt: "Time for This or That! I'll ask questions, you both answer, and we'll see how aligned you are. Ready?",
    sampleQuestions: [
      {
        question: "For a perfect weekend morning:",
        optionA: "Sleep in + brunch",
        optionB: "Early hike + sunrise"
      },
      {
        question: "Ideal vacation style:",
        optionA: "Planned itinerary",
        optionB: "Spontaneous adventure"
      },
      {
        question: "Creative evening:",
        optionA: "Cooking together",
        optionB: "Art gallery + wine"
      }
    ]
  },
  
  [InteractionType.ASSUMPTIONS]: {
    prompt: "Make an assumption about Nagpal! I'll get their real response. Fair warning - they might surprise you! 😄",
    followUp: "Interesting assumption! Let me get Nagpal to respond to that..."
  },
  
  [InteractionType.PROFILE_REACTION]: {
    prompt: "What did you think after going through Nagpal's profile? Give me one word that captures your reaction.",
    followUp: "Love that reaction! Want to ask them a question about something specific you noticed?"
  },
  
  [InteractionType.CUSTOM_QUESTION]: {
    prompt: "What would you like to ask Nagpal? I can deliver it personally and make sure you get a thoughtful response.",
    options: [
      "Get answer now (if they're around)",
      "Send as message (they'll respond when free)",
      "Add email for notification when answered"
    ]
  }
};

// Game Mechanics
export class InteractionManager {
  private gameState: GameState | null = null;
  
  startInteraction(type: InteractionType, participants: ('universe' | 'nagpal' | 'visitor')[]): GameState {
    this.gameState = {
      type,
      active: true,
      participants,
      currentTurn: 'universe', // Universe usually starts
      round: 1,
      startTime: new Date(),
      lastActivity: new Date()
    };
    
    // Set type-specific defaults
    switch (type) {
      case InteractionType.THIS_OR_THAT:
        this.gameState.maxRounds = 5;
        this.gameState.score = { nagpal: 0, visitor: 0 };
        this.gameState.data = { questions: [], currentQuestionIndex: 0, matches: 0 };
        break;
        
      case InteractionType.ASSUMPTIONS:
        this.gameState.maxRounds = 3;
        this.gameState.data = { assumptions: [], totalPoints: 0 };
        break;
        
      case InteractionType.PLAN_DATE:
        this.gameState.data = {
          location: '',
          timeOfDay: '',
          budget: '',
          vibe: '',
          visitorSuggestions: [],
          nagpalPreferences: []
        };
        break;
    }
    
    return this.gameState;
  }
  
  updateGameState(updates: Partial<GameState>): GameState | null {
    if (!this.gameState) return null;
    
    this.gameState = {
      ...this.gameState,
      ...updates,
      lastActivity: new Date()
    };
    
    return this.gameState;
  }
  
  endInteraction(): void {
    if (this.gameState) {
      this.gameState.active = false;
    }
  }
  
  getCurrentState(): GameState | null {
    return this.gameState;
  }
  
  // Helper methods for specific interactions
  addThisOrThatResponse(choice: string, participant: 'nagpal' | 'visitor', reasoning?: string): void {
    if (!this.gameState || this.gameState.type !== InteractionType.THIS_OR_THAT) return;
    
    const data = this.gameState.data as ThisOrThatData;
    const currentQuestion = data.questions[data.currentQuestionIndex];
    
    if (participant === 'nagpal') {
      currentQuestion.nagpalChoice = choice;
    } else {
      currentQuestion.visitorChoice = choice;
    }
    
    if (reasoning) {
      currentQuestion.reasoning = reasoning;
    }
    
    // Check if both answered
    if (currentQuestion.nagpalChoice && currentQuestion.visitorChoice) {
      if (currentQuestion.nagpalChoice === currentQuestion.visitorChoice) {
        data.matches++;
        this.gameState.score!.nagpal++;
        this.gameState.score!.visitor++;
      }
    }
  }
  
  addAssumption(assumption: string, correct: boolean, response: string, points: number): void {
    if (!this.gameState || this.gameState.type !== InteractionType.ASSUMPTIONS) return;
    
    const data = this.gameState.data as AssumptionsData;
    data.assumptions.push({
      assumption,
      correct,
      nagpalResponse: response,
      points
    });
    data.totalPoints += points;
  }
  
  updateDatePlan(updates: Partial<DatePlanningData>): void {
    if (!this.gameState || this.gameState.type !== InteractionType.PLAN_DATE) return;
    
    this.gameState.data = {
      ...this.gameState.data,
      ...updates
    };
  }
}

// Interaction Triggers - when Universe should start specific interactions
export const InteractionTriggers = {
  // After certain profile actions
  afterProfileView: [InteractionType.UNIVERSE_INTRO, InteractionType.PROFILE_REACTION],
  afterCardClick: [InteractionType.ICEBREAKER, InteractionType.CUSTOM_QUESTION],
  
  // Based on conversation length
  shortConversation: [InteractionType.THIS_OR_THAT, InteractionType.ASSUMPTIONS],
  mediumConversation: [InteractionType.PLAN_DATE, InteractionType.DEEP_DIVE],
  longConversation: [InteractionType.PLAN_TRIP, InteractionType.FUTURE_PLANNING],
  
  // Time-based
  firstInteraction: [InteractionType.UNIVERSE_INTRO],
  followUpSessions: [InteractionType.DELAYED_RESPONSE, InteractionType.CUSTOM_QUESTION],
  
  // Content-based triggers
  travelMentioned: [InteractionType.PLAN_TRIP],
  foodMentioned: [InteractionType.PLAN_DATE],
  creativityMentioned: [InteractionType.STORYTELLING],
  
  // Social signals
  highEngagement: [InteractionType.NAGPAL_SUMMON, InteractionType.DEEP_DIVE],
  needsIcebreaker: [InteractionType.THIS_OR_THAT, InteractionType.ASSUMPTIONS],
  
  // Boundary situations
  inappropriateContent: [InteractionType.END_CONVERSATION],
  lostInterest: [InteractionType.ICEBREAKER, InteractionType.UNIVERSE_INTRO]
};

export default InteractionManager;